import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { requisicaoAPI, dependenteAPI, formatters } from '../services/api';
import { Modal, ModalTitle, ModalBody, ModalFooter } from './Modal';
import { LuIdCard, LuLoaderCircle, LuUser, LuTrash2, LuPlus, LuDog } from 'react-icons/lu';
import { cartoesAPI } from '../services/api';
import { toast } from 'react-toastify';
import { downloadBlob } from '../utils/downloadUtils';

const MENSAGENS_PROTOCOLO_DEPENDENTE = {
  inclusao: {
    titulo: 'Inclusão de dependente',
    texto: 'Recebemos sua solicitação de inclusão de dependente. Um protocolo foi gerado para que a equipe analise o pedido.',
  },
  remocao: {
    titulo: 'Remoção de dependente',
    texto: 'Recebemos sua solicitação de remoção de dependente. Um protocolo foi gerado para que a equipe analise o pedido.',
  },
};

const DependentsSection = styled.div`
  margin-bottom: 30px;
`;

// --- ESTILOS PREMIUM REFINADOS ---
const DependentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const DependentCard = styled.div`
  background: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.isInactive ? '#fecaca' : '#e2e8f0'};
  border-left: 5px solid ${props => props.baseAccentColor};
  border-radius: 16px;
  padding: 20px;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.accentColor};
    border-left-color: ${props => props.accentColor};
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-left-width: 4px;
  }
`;

const StatusWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`;

const DependentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-right: 60px; // Espaço para o status
`;

const Avatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: ${props => props.bgColor};
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const DependentName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  line-height: 1.2;
`;

const MemberType = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.grayMedium};
  font-weight: 600;
  margin-top: 2px;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status?.toLowerCase()) {
      case 'ativo':
        return `background: #dcfce7; color: #166534;`;
      case 'inativo':
        return `background: #fee2e2; color: #991b1b;`;
      default:
        return `background: #f1f5f9; color: #475569;`;
    }
  }}
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #f1f5f9;
  border-bottom: 1px solid #f1f5f9;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoLabel = styled.div`
  color: ${props => props.theme.colors.grayMedium};
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
`;

const InfoValue = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-size: 13px;
  font-weight: 600;
  margin-top: 1px;
`;

const ActionArea = styled.div`
  display: flex;
  gap: 8px;
  margin-top: auto;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;

  &.primary {
    background: #f8fafc;
    color: ${props => props.theme.colors.secondary};
    border-color: #e2e8f0;
    
    &:hover:not(:disabled) {
      background: ${props => props.theme.colors.secondary};
      color: white;
      border-color: ${props => props.theme.colors.secondary};
    }
  }

  &.danger {
    background: #fff5f5;
    color: #e53e3e;
    border-color: #fed7d7;
    
    &:hover:not(:disabled) {
      background: #e53e3e;
      color: white;
      border-color: #e53e3e;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ModalButton = styled.button`
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
  transition: all 0.3s ease;
  min-width: 140px;

  &.confirm {
    background: ${({ theme }) => theme.colors.gradientPrimary};
    color: ${({ theme }) => theme.colors.white};
    box-shadow: 0 10px 15px -3px rgba(30, 64, 175, 0.25);
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 15px 20px -3px rgba(30, 64, 175, 0.35);
    }
  }

  &.cancel {
    background: #f1f5f9;
    color: #64748b;
    &:hover:not(:disabled) {
      background: #e2e8f0;
      color: #1e293b;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.theme.colors.grayMedium};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
    background: ${props => props.theme.colors.white};
    box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const AgeInfo = styled.div`
  padding: 14px 18px;
  background: #f0fdf4;
  border: 1px solid #dcfce7;
  border-radius: 10px;
  margin-top: 4px;
  margin-bottom: 20px;
  width: 100%;
  box-sizing: border-box;
  font-size: 14px;
  color: #166534;
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-weight: 600;
  flex-wrap: wrap;

  .age-display {
    display: flex;
    align-items: center;
    gap: 8px;

    &::before {
      content: '';
      width: 6px;
      height: 6px;
      background: #10b981;
      border-radius: 50%;
      flex-shrink: 0;
    }
  }

  .price-display {
    font-size: 15px;
    color: #15803d;
  }
`;

const AddDependentButton = styled.button`
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.primary};
  border: 2px dashed ${props => props.theme.colors.border};
  padding: 24px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  transition: all 0.3s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 24px auto;
  width: 100%;
  max-width: 500px;

  &:hover {
    background: ${props => props.theme.colors.lightBlue};
    border-color: ${props => props.theme.colors.secondary};
    color: ${props => props.theme.colors.secondary};
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.1);
  }

  .plus-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: ${props => props.theme.colors.lightBlue};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: ${props => props.theme.colors.primary};
    transition: all 0.3s ease;
  }

  &:hover .plus-icon {
    background: ${props => props.theme.colors.secondary};
    color: white;
  }

  @media (max-width: 768px) {
    flex-direction: row;
    padding: 16px;
    gap: 16px;
    justify-content: flex-start;
    text-align: left;

    .plus-icon {
      width: 40px;
      height: 40px;
      font-size: 18px;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${props => props.theme.colors.grayMedium};
  
  p {
    margin-bottom: 20px;
    font-size: 16px;
  }
`;

const TypeSelectorContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
`;

const RadioLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  padding: 20px;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  transition: all 0.3s ease;
  background: #f8fafc;
  text-align: center;
  position: relative;

  &.selected {
    border-color: ${({ theme }) => theme.colors.secondary};
    background-color: #fffbeb;
    color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 8px 20px rgba(245, 158, 11, 0.15);

    &::after {
      content: '✓';
      position: absolute;
      top: 8px;
      right: 8px;
      width: 20px;
      height: 20px;
      background: ${({ theme }) => theme.colors.secondary};
      color: white;
      border-radius: 50%;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &:hover:not(.selected) {
    border-color: #cbd5e1;
    background-color: #f1f5f9;
  }

  .type-icon {
    font-size: 28px;
    margin-bottom: 4px;
  }

  .type-text {
    font-size: 14px;
    font-weight: 700;
  }
`;

const RadioInput = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
`;

const DependentsGridComponent = ({ dependentes, refetch, contrato, showAddButton = true, openAddNonce = 0 }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    dependent: null,
  });

  const initialDependentState = {
    dependentType: '',
    name: '',
    birthDate: '',
    relationship: '',
    age: null,
    price: 0,
  };

  const [newDependent, setNewDependent] = useState(initialDependentState);
  const dependentTypeRef = useRef('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [protocolConfirm, setProtocolConfirm] = useState(null);

  const formatDateBR = (date) => {
    if (!date) return 'N/A';
    const ddmmyyyy = /^\d{2}\/\d{2}\/\d{4}$/;
    if (typeof date === 'string' && ddmmyyyy.test(date)) {
      return date;
    }
    const parsed = new Date(date);
    if (isNaN(parsed)) return 'N/A';
    return new Intl.DateTimeFormat('pt-BR').format(parsed);
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  };

  const openModal = (type, dependent = null) => {
    if (type === 'add') {
      dependentTypeRef.current = '';
      setNewDependent(initialDependentState);
    }
    setModalState({ isOpen: true, type, dependent });
  };

  const lastOpenAddNonce = useRef(0);
  useEffect(() => {
    if (openAddNonce && openAddNonce !== lastOpenAddNonce.current) {
      lastOpenAddNonce.current = openAddNonce;
      dependentTypeRef.current = '';
      setNewDependent({
        dependentType: '',
        name: '',
        birthDate: '',
        relationship: '',
        age: null,
        price: 0,
      });
      setModalState({ isOpen: true, type: 'add', dependent: null });
    }
  }, [openAddNonce]);

  const closeModal = () => {
    setModalState({ isOpen: false, type: null, dependent: null });
  };

  const handleGenerateSingleCard = async (dependent) => {
    if (!dependent || !contrato) {
      toast.error("Número do contrato não encontrado. Não é possível gerar o cartão.");
      return;
    }
    setGeneratingId(dependent.id);
    try {
      toast.info('Gerando cartão, por favor aguarde...');
      const response = await cartoesAPI.gerar([{ nome: dependent.nome }], contrato);

      // Criar blob do PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `cartao-${dependent.nome.replace(/\s+/g, '_')}-${contrato}.pdf`;

      // Usar função utilitária compatível com iOS
      await downloadBlob(blob, filename);

      toast.success('Cartão gerado com sucesso!');
    } catch (error) {
      console.error("Falha na geração do cartão individual:", error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao gerar cartão';
      toast.error(`Erro ao gerar cartão: ${errorMessage}`);
    } finally {
      setGeneratingId(null);
    }
  };

  const handleConfirmRemove = async () => {
    if (!modalState.dependent) return;
    setIsSubmitting(true);
    try {
      const payload = {
        tipo: 'REMOCAO_DEPENDENTE',
        dados: {
          nome: modalState.dependent.nome,
          cpf: modalState.dependent.cpf,
          plano: modalState.dependent.plano,
        }
      };

      await requisicaoAPI.criar(payload);

      closeModal();
      setProtocolConfirm('remocao');
      if (refetch) refetch();
    } catch (error) {
      toast.error('Erro ao enviar a solicitação de remoção. Tente novamente.');
      console.error('Erro na solicitação de remoção:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormChange = useCallback(async (field, value) => {
    if (field === 'dependentType') {
      dependentTypeRef.current = value;
      setNewDependent({
        ...initialDependentState,
        dependentType: value,
      });
      return;
    }

    if (field !== 'birthDate') {
      setNewDependent(prev => ({ ...prev, [field]: value }));
      return;
    }

    if (!value) {
      setNewDependent(prev => ({ ...prev, birthDate: '', age: null, price: 0 }));
      return;
    }

    const age = calculateAge(value);
    const dependentType = dependentTypeRef.current || 'humano';

    setNewDependent(prev => ({ ...prev, birthDate: value, age, price: null }));

    try {
      const priceFromApi = await dependenteAPI.getPriceByAge(age, dependentType);
      setNewDependent(prev => ({ ...prev, price: priceFromApi.data.data.preco || 0 }));
    } catch (error) {
      console.error("Erro ao buscar preço do dependente:", error);
      setNewDependent(prev => ({ ...prev, price: 0 }));
    }
  }, []);

  const handleConfirmAdd = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        tipo: 'INCLUSAO_DEPENDENTE',
        dados: newDependent
      };

      await requisicaoAPI.criar(payload);

      closeModal();
      setProtocolConfirm('inclusao');
    } catch (error) {
      toast.error('Erro ao enviar a solicitação de inclusão. Tente novamente.');
      console.error('Erro na solicitação de inclusão:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

  const isHumanDependent = newDependent.dependentType === 'humano';
  const isAddFormValid = newDependent.name && newDependent.birthDate;


  return (
    <DependentsSection>
      {(!dependentes || dependentes.length === 0) ? (
        <EmptyState>
          <p>Nenhum dependente cadastrado.</p>
        </EmptyState>
      ) : (
        <DependentsGrid>
          {dependentes.map((dependente) => {
            const isInactive = dependente.status !== 'ATIVO';
            const isPet = dependente.pet === true || dependente.pet === 1 || dependente.tipoDependente === 'pet';
            const planConfig = formatters.getPlanConfig(dependente.idPlano || dependente.planoId || '726', isPet ? "PET" : "HUM");
            const isGeneratingThisCard = generatingId === dependente.id;

            return (
              <DependentCard 
                key={dependente.id || dependente.cpf} 
                isInactive={isInactive} 
                accentColor={isPet ? '#f59e0b' : '#1e3a8a'}
                baseAccentColor={isPet ? '#f59e0b54' : '#1e3a8a2e'}
              >
                {/*
                -- Desativado temporariamente para não confundir o usuário --
                <StatusWrapper>
                  <StatusBadge status={dependente.status}>
                    {dependente.status || 'Ativo'}
                  </StatusBadge>
                </StatusWrapper>
                */}

                <DependentHeader>
                  <Avatar 
                    color={isInactive ? '#ef4444' : (isPet ? '#d97706' : '#1e3a8a')} 
                    bgColor={isInactive ? '#fef2f2' : (isPet ? '#fffbeb' : '#dbeafe')}
                  >
                    {isPet ? <LuDog /> : <LuUser />}
                  </Avatar>
                  <HeaderInfo>
                    <DependentName>{dependente.nome || 'N/A'}</DependentName>
                    <MemberType>{isPet ? 'Dependente Pet' : (dependente.parentesco || 'Dependente')}</MemberType>
                  </HeaderInfo>
                </DependentHeader>

                <InfoGrid>
                  {!isPet && (
                    <InfoItem>
                      <InfoLabel>CPF</InfoLabel>
                      <InfoValue>{dependente.cpf ? formatters.formatDocumentoOculto(dependente.cpfOculto) : 'N/A'}</InfoValue>
                    </InfoItem>
                  )}

                  <InfoItem>
                    <InfoLabel>{isPet ? 'Idade Pet' : 'Idade'}</InfoLabel>
                    <InfoValue>
                      {dependente.idade ? `${dependente.idade} ${dependente.idade === 1 ? 'ano' : 'anos'}` : `N/A`}
                    </InfoValue>
                  </InfoItem>

                  {!isPet && (
                    <InfoItem>
                      <InfoLabel>Parentesco</InfoLabel>
                      <InfoValue>{dependente.parentesco || 'N/A'}</InfoValue>
                    </InfoItem>
                  )}

                  <InfoItem>
                    <InfoLabel>Sexo</InfoLabel>
                    <InfoValue>{dependente.sexo === 'M' ? 'Masc.' : dependente.sexo === 'F' ? 'Fem.' : 'N/A'}</InfoValue>
                  </InfoItem>
                </InfoGrid>

                <ActionArea>
                  {!isInactive && (
                    <ActionButton
                      className="primary"
                      onClick={() => handleGenerateSingleCard(dependente)}
                      disabled={generatingId !== null}
                    >
                      {isGeneratingThisCard ? (
                        <LuLoaderCircle className="spinner" />
                      ) : (
                        <LuIdCard />
                      )}
                      {isGeneratingThisCard ? 'Gerando...' : 'Cartão'}
                    </ActionButton>
                  )}
                  
                  {!isInactive && (
                    <ActionButton className="danger" onClick={() => openModal('remove', dependente)}>
                      <LuTrash2 size={14} /> Remover
                    </ActionButton>
                  )}
                </ActionArea>
              </DependentCard>
            );
          })}
        </DependentsGrid>
      )}

      {showAddButton && (
        <AddDependentButton type="button" onClick={() => openModal('add')}>
          <div className="plus-icon"><LuPlus /></div>
          Solicitar Inclusão de Dependente
        </AddDependentButton>
      )}

      <Modal isOpen={modalState.isOpen} onClose={closeModal}>
        {/* O conteúdo do Modal permanece o mesmo */}
        {modalState.type === 'remove' && (
          <>
            <ModalTitle>Confirmar Remoção</ModalTitle>
            <ModalBody>
              <p>Você está prestes a solicitar a remoção do dependente <strong>{modalState.dependent?.nome}</strong>.</p>
              <p style={{ fontWeight: 'bold', color: '#c00' }}>
                Atenção: Esta ação não é imediata. Sua solicitação será enviada para análise do setor de cadastro.
              </p>
            </ModalBody>
            <ModalFooter>
              <ModalButton className="cancel" onClick={closeModal} disabled={isSubmitting}>Cancelar</ModalButton>
              <ModalButton className="confirm" onClick={handleConfirmRemove} disabled={isSubmitting}>
                {isSubmitting ? 'Enviando...' : 'Enviar Solicitação'}
              </ModalButton>
            </ModalFooter>
          </>
        )}

        {modalState.type === 'add' && (
          <>
            <ModalTitle>Solicitar Inclusão de Dependente</ModalTitle>
            <ModalBody>
              <FormGroup>
                <Label>Tipo de Dependente</Label>
                <TypeSelectorContainer>
                  <RadioLabel className={newDependent.dependentType === 'humano' ? 'selected' : ''}>
                    <RadioInput
                      type="radio"
                      name="dependentType"
                      value="humano"
                      checked={newDependent.dependentType === 'humano'}
                      onChange={(e) => handleFormChange('dependentType', e.target.value)}
                    />
                    <LuUser className="type-icon" />
                    <span className="type-text">Humano</span>
                  </RadioLabel>
                  <RadioLabel className={newDependent.dependentType === 'pet' ? 'selected' : ''}>
                    <RadioInput
                      type="radio"
                      name="dependentType"
                      value="pet"
                      checked={newDependent.dependentType === 'pet'}
                      onChange={(e) => handleFormChange('dependentType', e.target.value)}
                    />
                    <LuDog className="type-icon" />
                    <span className="type-text">Pet</span>
                  </RadioLabel>
                </TypeSelectorContainer>
              </FormGroup>

              {/* --- ALTERADO: Campos são exibidos apenas se um tipo for selecionado --- */}
              {newDependent.dependentType && (
                <>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="depName">Nome Completo {isHumanDependent ? '' : 'do Pet'}</Label>
                      <Input
                        type="text"
                        id="depName"
                        value={newDependent.name}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                        placeholder={isHumanDependent ? "Nome do dependente" : "Nome do pet"}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="depBirthDate">Data de Nascimento</Label>
                      <Input
                        type="date"
                        id="depBirthDate"
                        value={newDependent.birthDate}
                        onChange={(e) => handleFormChange('birthDate', e.target.value)}
                      />
                    </FormGroup>
                  </FormRow>
                  <AgeInfo show={newDependent.age !== null && newDependent.age >= 0 && newDependent.age < 150}>
                    <span className="age-display">
                      {newDependent.age === 0 ? 'Menos de 1 ano' : `${newDependent.age} anos`}
                    </span>
                    <span className="price-display">
                      {newDependent.price === null
                        ? 'Calculando valor...'
                        : `R$ ${(newDependent.price || 0).toFixed(2).replace('.', ',')} (Valor estimado)`
                      }
                    </span>
                  </AgeInfo>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <ModalButton className="cancel" onClick={closeModal} disabled={isSubmitting}>Cancelar</ModalButton>
              <ModalButton className="confirm" onClick={handleConfirmAdd} disabled={!isAddFormValid || isSubmitting}>
                {isSubmitting ? 'Enviando...' : 'Enviar Solicitação'}
              </ModalButton>
            </ModalFooter>
          </>
        )}
      </Modal>

      <Modal isOpen={!!protocolConfirm} onClose={() => setProtocolConfirm(null)}>
        <ModalTitle>{MENSAGENS_PROTOCOLO_DEPENDENTE[protocolConfirm]?.titulo}</ModalTitle>
        <ModalBody>
          <p>{MENSAGENS_PROTOCOLO_DEPENDENTE[protocolConfirm]?.texto}</p>
        </ModalBody>
        <ModalFooter>
          <ModalButton className="confirm" onClick={() => setProtocolConfirm(null)}>
            Entendi
          </ModalButton>
        </ModalFooter>
      </Modal>
    </DependentsSection>
  );
};

export default DependentsGridComponent;
